import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { toast } from 'sonner';
import {
  Users,
  Shield,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  User,
  Phone,
  MapPin,
  CheckCircle,
  Star,
  CreditCard,
  Heart,
  Clock,
  DollarSign,
  Navigation,
  Calendar,
  UserPlus
} from 'lucide-react';
import { formatDateToLocalString } from '@/lib/utils';

const RegisterPassengerPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',

    // Address Information
    homeAddress: '',
    workAddress: '',
    city: '',
    state: '',
    zipCode: '',

    // Payment Information
    paymentMethod: '',
    cardNumber: '',
    cardName: '',
    cardExpiry: '',
    cardCVV: '',

    // Emergency Contact
    emergencyName: '',
    emergencyPhone: '',
    emergencyRelationship: '',

    // Preferences
    preferredVehicleType: '',
    rideFrequency: '',
    purpose: '',
    accessibilityNeeds: '',
    additionalNotes: '',

    // Security
    password: '',
    confirmPassword: '',
    acceptTerms: false,
    acceptMarketing: false,
    enableLocationServices: false
  });

  const totalSteps = 3;
  const progress = (currentStep / totalSteps) * 100;

  const vehicleTypes = [
    { value: 'standard', label: 'Standard', desc: 'Comfortable and affordable', price: '$' },
    { value: 'comfort', label: 'Comfort', desc: 'Extra legroom and premium features', price: '$$' },
    { value: 'luxury', label: 'Luxury', desc: 'High-end vehicles and professional drivers', price: '$$$' },
    { value: 'suv', label: 'SUV', desc: 'Space for up to 6 passengers', price: '$$' },
    { value: 'wheelchair', label: 'Wheelchair Accessible', desc: 'Vehicles equipped with ramps/lifts', price: '$$' }
  ];

  const rideFrequencies = [
    'Daily',
    'Few times per week',
    'Once a week',
    'Few times per month',
    'Rarely/First time'
  ];

  const ridePurposes = [
    'Commuting to work',
    'Airport transfers',
    'Business meetings',
    'Social events',
    'Shopping/errands',
    'Medical appointments',
    'Tourism/leisure',
    'School runs',
    'Other'
  ];

  const relationships = [
    'Spouse/Partner',
    'Parent',
    'Child',
    'Sibling',
    'Friend',
    'Colleague',
    'Other'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateStep1 = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return false;
    }
    if (!formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!formData.dateOfBirth) {
      toast.error('Please enter your date of birth');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.homeAddress || !formData.city || !formData.state || !formData.zipCode) {
      toast.error('Please fill in all address information');
      return false;
    }
    if (!formData.paymentMethod || !formData.cardNumber || !formData.cardName) {
      toast.error('Please fill in payment information');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.password || !formData.confirmPassword) {
      toast.error('Please fill in password fields');
      return false;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    if (!formData.acceptTerms) {
      toast.error('Please accept the Terms of Service');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && validateStep2()) {
      setCurrentStep(3);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (validateStep3()) {
      toast.success('Registration successful! Welcome to Aboosto!');

      setTimeout(() => {
        navigate('/login');
      }, 2000);
    }
  };

  const passengerBenefits = [
    { icon: <DollarSign className="w-5 h-5" />, title: "Affordable Rates", desc: "Competitive pricing with no hidden fees" },
    { icon: <Clock className="w-5 h-5" />, title: "Quick Pickups", desc: "Average wait time under 5 minutes" },
    { icon: <Shield className="w-5 h-5" />, title: "Safe Rides", desc: "Verified drivers with safety features" },
    { icon: <Star className="w-5 h-5" />, title: "Rewards Program", desc: "Earn points and exclusive discounts" }
  ];

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
      <div className="w-full max-w-5xl">
        {/* Back to home */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to home
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Left side - Benefits */}
          <div className="space-y-6 lg:sticky lg:top-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center">
                  <Users className="w-7 h-7 text-primary-foreground" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">Ride with Aboosto</h1>
                  <div className="flex items-center space-x-2">
                    <Badge className="bg-success/10 text-success border-success/20">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Safe & Reliable
                    </Badge>
                    <Badge className="bg-primary/10 text-primary border-primary/20">
                      <Star className="w-3 h-3 mr-1" />
                      4.8 Rating
                    </Badge>
                  </div>
                </div>
              </div>

              <p className="text-lg text-muted-foreground">
                Your premium ride-hailing service with professional drivers, comfortable vehicles, and transparent pricing.
              </p>
            </div>

            <div className="space-y-4">
              {passengerBenefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4 p-4 rounded-lg bg-card border border-border/50 hover-lift">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
                    {benefit.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground">{benefit.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
              <div className="flex items-center space-x-2 text-primary mb-3">
                <Heart className="w-6 h-6" />
                <span className="font-semibold text-lg">First Ride Free</span>
              </div>
              <p className="text-sm text-primary/80 mb-4">
                New users get their first ride up to $15 completely free, no strings attached.
              </p>
              <ul className="space-y-2 text-sm text-primary/80">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Apply promo code: FIRSTRIDE</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Valid for 30 days after registration</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>No minimum fare requirements</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right side - Form */}
          <Card className="border-border/50 shadow-elegant">
            <CardHeader className="space-y-4 pb-6">
              <div className="space-y-2">
                <h2 className="text-xl font-semibold text-foreground">Passenger Registration</h2>
                <p className="text-sm text-muted-foreground">Step {currentStep} of {totalSteps}</p>
              </div>

              <Progress value={progress} className="h-2" />
            </CardHeader>

            <CardContent className="space-y-6">
              <form onSubmit={handleRegister} className="space-y-6">
                {/* Step 1: Personal Information */}
                {currentStep === 1 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-foreground">Personal Information</h3>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name *</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="firstName"
                            type="text"
                            placeholder="John"
                            className="pl-10"
                            value={formData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          type="text"
                          placeholder="Smith"
                          value={formData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="john.smith@email.com"
                          className="pl-10"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="+1 (555) 123-4567"
                          className="pl-10"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="dateOfBirth">Date of Birth *</Label>
                      <DatePicker
                        value={formData.dateOfBirth ? new Date(formData.dateOfBirth) : undefined}
                        onChange={(date) => handleInputChange('dateOfBirth', date ? formatDateToLocalString(date) : '')}
                        placeholder="Select date of birth"
                      />
                    </div>

                    <h3 className="text-lg font-medium text-foreground pt-4">Ride Preferences</h3>

                    <div className="space-y-2">
                      <Label>Preferred Vehicle Type *</Label>
                      <div className="space-y-2">
                        {vehicleTypes.map((type) => (
                          <div key={type.value} className="border rounded-lg p-3 cursor-pointer hover:bg-primary/5 transition-colors">
                            <div className="flex items-start space-x-3">
                              <Checkbox
                                checked={formData.preferredVehicleType === type.value}
                                onCheckedChange={(checked) => checked && handleInputChange('preferredVehicleType', type.value)}
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <div className="font-medium">{type.label}</div>
                                  <div className="text-sm font-medium text-primary">{type.price}</div>
                                </div>
                                <div className="text-sm text-muted-foreground">{type.desc}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="rideFrequency">How often do you ride? *</Label>
                        <Select value={formData.rideFrequency} onValueChange={(value) => handleInputChange('rideFrequency', value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                          <SelectContent>
                            {rideFrequencies.map((frequency) => (
                              <SelectItem key={frequency} value={frequency}>
                                {frequency}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="purpose">Primary Purpose *</Label>
                        <Select value={formData.purpose} onValueChange={(value) => handleInputChange('purpose', value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select purpose" />
                          </SelectTrigger>
                          <SelectContent>
                            {ridePurposes.map((purpose) => (
                              <SelectItem key={purpose} value={purpose}>
                                {purpose}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="accessibilityNeeds">Accessibility Requirements</Label>
                      <Textarea
                        id="accessibilityNeeds"
                        placeholder="Any special requirements we should know about (wheelchair access, service animals, etc.)"
                        className="min-h-20"
                        value={formData.accessibilityNeeds}
                        onChange={(e) => handleInputChange('accessibilityNeeds', e.target.value)}
                      />
                    </div>

                    <Button
                      type="button"
                      onClick={handleNext}
                      className="w-full bg-gradient-primary hover:opacity-90"
                    >
                      Continue to Address & Payment
                      <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                    </Button>
                  </div>
                )}

                {/* Step 2: Address & Payment */}
                {currentStep === 2 && (
                  <div className="space-y-4">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setCurrentStep(1)}
                      className="mb-4"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>

                    <h3 className="text-lg font-medium text-foreground">Address Information</h3>

                    <div className="space-y-2">
                      <Label htmlFor="homeAddress">Home Address *</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="homeAddress"
                          type="text"
                          placeholder="123 Main Street, Apt 4B"
                          className="pl-10"
                          value={formData.homeAddress}
                          onChange={(e) => handleInputChange('homeAddress', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="workAddress">Work Address</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="workAddress"
                          type="text"
                          placeholder="456 Business Ave, Suite 200"
                          className="pl-10"
                          value={formData.workAddress}
                          onChange={(e) => handleInputChange('workAddress', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          type="text"
                          placeholder="New York"
                          value={formData.city}
                          onChange={(e) => handleInputChange('city', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state">State *</Label>
                        <Input
                          id="state"
                          type="text"
                          placeholder="NY"
                          value={formData.state}
                          onChange={(e) => handleInputChange('state', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="zipCode">ZIP Code *</Label>
                        <Input
                          id="zipCode"
                          type="text"
                          placeholder="10001"
                          value={formData.zipCode}
                          onChange={(e) => handleInputChange('zipCode', e.target.value)}
                        />
                      </div>
                    </div>

                    <h3 className="text-lg font-medium text-foreground pt-4">Payment Information</h3>

                    <div className="space-y-2">
                      <Label htmlFor="paymentMethod">Payment Method *</Label>
                      <Select value={formData.paymentMethod} onValueChange={(value) => handleInputChange('paymentMethod', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select payment method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="credit">Credit Card</SelectItem>
                          <SelectItem value="debit">Debit Card</SelectItem>
                          <SelectItem value="apple">Apple Pay</SelectItem>
                          <SelectItem value="google">Google Pay</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {(formData.paymentMethod === 'credit' || formData.paymentMethod === 'debit') && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="cardNumber">Card Number *</Label>
                          <div className="relative">
                            <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              id="cardNumber"
                              type="text"
                              placeholder="1234 5678 9012 3456"
                              className="pl-10"
                              value={formData.cardNumber}
                              onChange={(e) => handleInputChange('cardNumber', e.target.value)}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="cardName">Cardholder Name *</Label>
                          <Input
                            id="cardName"
                            type="text"
                            placeholder="John Smith"
                            value={formData.cardName}
                            onChange={(e) => handleInputChange('cardName', e.target.value)}
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="cardExpiry">Expiry Date *</Label>
                            <Input
                              id="cardExpiry"
                              type="text"
                              placeholder="MM/YY"
                              value={formData.cardExpiry}
                              onChange={(e) => handleInputChange('cardExpiry', e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="cardCVV">CVV *</Label>
                            <Input
                              id="cardCVV"
                              type="text"
                              placeholder="123"
                              value={formData.cardCVV}
                              onChange={(e) => handleInputChange('cardCVV', e.target.value)}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <h3 className="text-lg font-medium text-foreground pt-4">Emergency Contact</h3>

                    <div className="space-y-2">
                      <Label htmlFor="emergencyName">Contact Name *</Label>
                      <Input
                        id="emergencyName"
                        type="text"
                        placeholder="Jane Smith"
                        value={formData.emergencyName}
                        onChange={(e) => handleInputChange('emergencyName', e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="emergencyPhone">Phone Number *</Label>
                        <Input
                          id="emergencyPhone"
                          type="tel"
                          placeholder="+1 (555) 987-6543"
                          value={formData.emergencyPhone}
                          onChange={(e) => handleInputChange('emergencyPhone', e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="emergencyRelationship">Relationship *</Label>
                        <Select value={formData.emergencyRelationship} onValueChange={(value) => handleInputChange('emergencyRelationship', value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select relationship" />
                          </SelectTrigger>
                          <SelectContent>
                            {relationships.map((relationship) => (
                              <SelectItem key={relationship} value={relationship}>
                                {relationship}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="additionalNotes">Additional Notes</Label>
                      <Textarea
                        id="additionalNotes"
                        placeholder="Any other information that would help us serve you better..."
                        className="min-h-20"
                        value={formData.additionalNotes}
                        onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
                      />
                    </div>

                    <Button
                      type="button"
                      onClick={handleNext}
                      className="w-full bg-gradient-primary hover:opacity-90"
                    >
                      Continue to Security
                      <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                    </Button>
                  </div>
                )}

                {/* Step 3: Security & Terms */}
                {currentStep === 3 && (
                  <div className="space-y-4">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setCurrentStep(2)}
                      className="mb-4"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>

                    <h3 className="text-lg font-medium text-foreground">Account Security</h3>

                    <div className="space-y-2">
                      <Label htmlFor="password">Password *</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Create a password (min 6 chars)"
                          className="pl-10 pr-10"
                          value={formData.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirm Password *</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="Confirm your password"
                          className="pl-10 pr-10"
                          value={formData.confirmPassword}
                          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="terms"
                          checked={formData.acceptTerms}
                          onCheckedChange={(checked) => handleInputChange('acceptTerms', checked)}
                          className="mt-1"
                        />
                        <Label htmlFor="terms" className="text-sm leading-5">
                          I agree to the <Link to="/terms" className="text-primary hover:text-primary/80">Passenger Terms of Service</Link> and <Link to="/privacy" className="text-primary hover:text-primary/80">Privacy Policy</Link>
                        </Label>
                      </div>

                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="location"
                          checked={formData.enableLocationServices}
                          onCheckedChange={(checked) => handleInputChange('enableLocationServices', checked)}
                          className="mt-1"
                        />
                        <Label htmlFor="location" className="text-sm leading-5">
                          Enable location services for faster pickups and accurate ETAs
                        </Label>
                      </div>

                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="marketing"
                          checked={formData.acceptMarketing}
                          onCheckedChange={(checked) => handleInputChange('acceptMarketing', checked)}
                          className="mt-1"
                        />
                        <Label htmlFor="marketing" className="text-sm leading-5">
                          Send me promotions, ride discounts, and travel tips
                        </Label>
                      </div>
                    </div>

                    <Button type="submit" className="w-full bg-gradient-primary hover:opacity-90 h-12">
                      <UserPlus className="w-5 h-5 mr-2" />
                      Create Passenger Account
                    </Button>
                  </div>
                )}
              </form>

              <div className="text-center text-sm">
                <span className="text-muted-foreground">Already have an account? </span>
                <Link to="/login" className="text-primary hover:text-primary/80 font-medium transition-colors">
                  Sign in
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RegisterPassengerPage;